// New-chore sheet — single entry point for creating BOTH kinds of
// chores. A pill at the top toggles between "Kid" and "Adult" mode;
// the body swaps form fields to match (kid chores have icons + an
// approval flag; adult chores have categories + a color hue + a
// "rotates" assignee option). Each side dispatches to its own RPC
// (chores.create vs adultChores.create).
//
// Defaulting: if the household has at least one kid we default to
// "Adult" mode (more frequent use case for parents), but the
// dropdown's assignee selection retargets the mode when the
// selected assignee is a kid. Conversely, if all members are
// adults, the Kid pill is hidden entirely.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import {
  api,
  type AdultChore,
  type AdultChoreFrequency,
  type ChoreFrequency,
  type HouseholdMember,
} from "../../lib/rpc";

interface NewChoreSheetProps {
  members: HouseholdMember[];
  /** Closes the sheet — wired to backdrop click, ESC, Cancel button. */
  onCancel: () => void;
  /** Fired after a successful create OR update. The parent refreshes
   *  both chore lists and toasts. We pass the kind through so the
   *  toast copy can match ("Kid chore added" vs "Chore added"). */
  onSaved: (kind: "kid" | "adult") => void;
  /** When set, the sheet renders as an EDIT of this adult chore
   *  rather than a fresh create. Pre-fills the adult fields with the
   *  current values, hides the Kid/Adult mode toggle (you can't change
   *  a chore's kind via edit), and calls adultChores.update on save. */
  editingAdultChore?: AdultChore | null;
  /** When set, edits a kid chore instead — sourced from UnifiedChore
   *  so the All view can hand any of its rows here. Fields not
   *  carried on UnifiedChore (rrule) are left undefined and patch
   *  semantics skip them. */
  editingKidChore?: {
    rawId: number;
    name: string;
    icon: string | null;
    assignee: string | null;
    assignmentMode: "single" | "rotating" | "parallel";
    assignees: string[];
    frequency: string;
    stars: number;
    requiresApproval: boolean;
  } | null;
}

type Mode = "kid" | "adult";

const HUE_CHOICES = [10, 30, 50, 75, 95, 120, 145, 195, 220, 280, 320, 340];
const ADULT_FREQUENCIES: { id: AdultChoreFrequency; label: string }[] = [
  { id: "daily",     label: "Daily" },
  { id: "weekly",    label: "Weekly" },
  { id: "monthly",   label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "once",      label: "Just once" },
];
const KID_FREQUENCIES: { id: ChoreFrequency; label: string }[] = [
  { id: "daily",    label: "Every day" },
  { id: "weekly",   label: "Once a week" },
  { id: "biweekly", label: "Every 2 weeks" },
];
const KID_ICONS = [
  "🧺", "🧹", "🧼", "🛏️", "🍽️", "🚮", "🐕", "🐈", "🪴", "🌱",
  "📚", "✏️", "📖", "🎒", "🧸", "🪥", "🚿", "🛁", "🧴", "🧽",
  "🍳", "🥕", "🍎", "🚲", "⚽", "🎨", "🎵", "💪", "🌟", "✨",
];
const ADULT_ICONS = [
  // Bathroom
  "🪥", "🚿", "🛁", "🚽", "🧴", "🧼", "🪒",
  // Kitchen / dishes
  "🍽️", "🍳", "🥘", "🧊", "🛒", "☕",
  // Laundry / clothes
  "👕", "🧺", "🧦", "🧵",
  // Cleaning
  "🧹", "🧽", "🚮", "🗑️", "🪟",
  // Garden / outdoors
  "🪴", "🌱", "🌿", "🍂",
  // Pets
  "🐕", "🐈", "🐾",
  // Home / utility
  "🛏️", "🔧", "🔨", "🧰", "💡", "📬", "📦",
  // Car / travel
  "🚗", "⛽", "🛞",
  // Misc
  "💧", "🔥", "📅",
];

export function NewChoreSheet({ members, onCancel, onSaved, editingAdultChore, editingKidChore }: NewChoreSheetProps) {
  const adults = useMemo(() => members.filter((m) => m.role !== "kid"), [members]);
  const kids = useMemo(() => members.filter((m) => m.role === "kid"), [members]);
  const isEditing = !!editingAdultChore || !!editingKidChore;
  const isEditingKid = !!editingKidChore;
  const isEditingAdult = !!editingAdultChore;

  // Mode forces to the editing kind when editing; defaults to kid if
  // there are kids in the household, else adult.
  const [mode, setMode] = useState<Mode>(
    isEditingKid ? "kid" : isEditingAdult ? "adult" : (kids.length > 0 ? "kid" : "adult"),
  );

  // Shared field — pre-fill from whichever chore is being edited.
  const [name, setName] = useState(editingAdultChore?.name || editingKidChore?.name || "");

  // Kid fields — pre-fill from editingKidChore on edit.
  const [kidIcon, setKidIcon] = useState(editingKidChore?.icon || "🧺");
  const [kidAssignee, setKidAssignee] = useState(
    editingKidChore?.assignmentMode === "single"
      ? (editingKidChore.assignee || "")
      : (kids[0]?.userId || adults[0]?.userId || ""),
  );
  // Kid-side assignment mode: "single" (one assignee), "parallel"
  // (each listed kid tracks their own cycle — "all kids brush teeth"),
  // or "rotating" (ordered roster; week N goes to roster[N % len] so
  // adults can fairly cycle a chore across a subset of kids).
  const [kidMode, setKidMode] = useState<"single" | "parallel" | "rotating">(
    editingKidChore?.assignmentMode === "parallel"
      ? "parallel"
      : editingKidChore?.assignmentMode === "rotating"
        ? "rotating"
        : "single",
  );
  // Same roster column drives parallel + rotating — for rotating the
  // ORDER matters (it's the rotation sequence), for parallel it doesn't.
  const [kidAssignees, setKidAssignees] = useState<string[]>(
    (editingKidChore?.assignmentMode === "parallel" || editingKidChore?.assignmentMode === "rotating")
      && editingKidChore.assignees.length > 0
      ? editingKidChore.assignees
      : kids.map((k) => k.userId),
  );
  const [kidFrequency, setKidFrequency] = useState<ChoreFrequency>(
    (editingKidChore?.frequency as ChoreFrequency) || "daily",
  );
  const [kidStars, setKidStars] = useState(editingKidChore?.stars ?? 1);
  const [kidRequiresApproval, setKidRequiresApproval] = useState(editingKidChore?.requiresApproval ?? false);

  // Adult fields — pre-fill from the chore on edit, otherwise the
  // create-time defaults.
  const [adultCategory, setAdultCategory] = useState(editingAdultChore?.category || "");
  const [adultIcon, setAdultIcon] = useState<string | null>(editingAdultChore?.icon || null);
  const [adultFrequency, setAdultFrequency] = useState<AdultChoreFrequency>(
    (editingAdultChore?.frequency as AdultChoreFrequency) || "weekly",
  );
  // Three-way assignment mode: single (one person), rotating (week
  // parity), parallel (each listed assignee owns the chore on their
  // own cycle — the "both brush teeth" case).
  const [adultMode, setAdultMode] = useState<"single" | "rotating" | "parallel">(
    editingAdultChore?.assignmentMode || (adults.length >= 2 ? "rotating" : "single"),
  );
  const [adultAssignee, setAdultAssignee] = useState(
    editingAdultChore?.assignmentMode === "single"
      ? editingAdultChore.assignee
      : (adults[0]?.userId || ""),
  );
  const [adultAssignees, setAdultAssignees] = useState<string[]>(
    editingAdultChore?.assignmentMode === "parallel" && editingAdultChore.assignees.length > 0
      ? editingAdultChore.assignees
      : adults.map((a) => a.userId),
  );
  const [adultHue, setAdultHue] = useState(editingAdultChore?.hue ?? 220);
  const [adultStars, setAdultStars] = useState(editingAdultChore?.stars ?? 0);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const canSubmit = !!name.trim() && !saving && (
    mode === "kid"
      ? (kidMode === "parallel"
          ? kidAssignees.length >= 1
          : kidMode === "rotating"
            ? kidAssignees.length >= 2
            : !!kidAssignee)
      : adultMode === "parallel"
        ? adultAssignees.length >= 1
        : adultMode === "single"
          ? !!adultAssignee
          : true  // adult rotating uses the household pool
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setErr(null);
    try {
      // Build the adult-chore patch / create payload from the three-
      // way assignment-mode state. The server validates that parallel
      // mode has ≥2 assignees and that everyone's a household member.
      const adultPayload = {
        name: name.trim(),
        icon: adultIcon,
        category: adultCategory.trim() || null,
        frequency: adultFrequency,
        hue: adultHue,
        stars: adultStars,
        assignmentMode: adultMode,
        ...(adultMode === "single" ? { assignee: adultAssignee } : {}),
        ...(adultMode === "parallel" ? { assignees: adultAssignees } : {}),
      };
      if (isEditingAdult && editingAdultChore) {
        await api.adultChoresUpdate(editingAdultChore.id, adultPayload);
        onSaved("adult");
        return;
      }
      if (isEditingKid && editingKidChore) {
        // Kid edit — same field set the create path uses. Server
        // accepts a partial patch and atomically re-derives the
        // assignment-mode triple just like adultChoresUpdate.
        await api.choresUpdate(editingKidChore.rawId, {
          name: name.trim(),
          icon: kidIcon,
          assignmentMode: kidMode,
          ...(kidMode === "single" ? { assignedToUserId: kidAssignee || null } : { assignees: kidAssignees }),
          frequency: kidFrequency,
          stars: kidStars,
          requiresApproval: kidRequiresApproval,
        });
        onSaved("kid");
        return;
      }
      if (mode === "kid") {
        await api.choresCreate({
          name: name.trim(),
          icon: kidIcon,
          assignmentMode: kidMode,
          ...(kidMode === "single" ? { assignedToUserId: kidAssignee || null } : { assignees: kidAssignees }),
          frequency: kidFrequency,
          stars: kidStars,
          requiresApproval: kidRequiresApproval,
        });
      } else {
        await api.adultChoresCreate(adultPayload);
      }
      onSaved(mode);
    } catch (e: any) {
      setErr(e?.message || "couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cr-bg" onClick={onCancel}>
      <div className="cr-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="New chore">
        <div className="cr-handle" />
        <header className="cr-head">
          <div>
            <h3 className="cr-title">{isEditing ? "Edit chore" : "New chore"}</h3>
            <div className="cr-sub">
              {isEditing ? "Tweak whatever needs changing." : "Pop it in. You can edit later."}
            </div>
          </div>
          <button className="icon-btn" type="button" onClick={onCancel} title="Close">
            <Icon name="x" />
          </button>
        </header>

        {/* Kid/Adult mode toggle — hidden when the household has no
            kids, since the kid mode has no useful assignees then. Also
            hidden when editing — an adult chore can't be edited into a
            kid chore. */}
        {kids.length > 0 && !isEditing && (
          <div className="cr-mode-toggle">
            <button
              type="button"
              data-active={mode === "adult"}
              onClick={() => setMode("adult")}
            >Adult chore</button>
            <button
              type="button"
              data-active={mode === "kid"}
              onClick={() => setMode("kid")}
            >Kid chore</button>
          </div>
        )}

        <div className="cr-body">
          {err && <div className="cr-err">{err}</div>}

          <label className="cr-row">
            <span>What is it</span>
            <input
              className="input"
              placeholder={mode === "kid" ? "e.g. Make the bed" : "e.g. Wipe the bathroom mirror"}
              value={name}
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
              maxLength={80}
              autoFocus
            />
          </label>

          {mode === "kid" ? (
            <KidFields
              icon={kidIcon} setIcon={setKidIcon}
              assignmentMode={kidMode} setAssignmentMode={setKidMode}
              assignee={kidAssignee} setAssignee={setKidAssignee}
              assignees={kidAssignees} setAssignees={setKidAssignees}
              frequency={kidFrequency} setFrequency={setKidFrequency}
              stars={kidStars} setStars={setKidStars}
              requiresApproval={kidRequiresApproval} setRequiresApproval={setKidRequiresApproval}
              members={members}
              kids={kids}
            />
          ) : (
            <AdultFields
              category={adultCategory} setCategory={setAdultCategory}
              icon={adultIcon} setIcon={setAdultIcon}
              frequency={adultFrequency} setFrequency={setAdultFrequency}
              assignmentMode={adultMode} setAssignmentMode={setAdultMode}
              assignee={adultAssignee} setAssignee={setAdultAssignee}
              assignees={adultAssignees} setAssignees={setAdultAssignees}
              hue={adultHue} setHue={setAdultHue}
              stars={adultStars} setStars={setAdultStars}
              adults={adults}
            />
          )}
        </div>

        <footer className="cr-foot">
          <button className="btn" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" type="button" onClick={submit} disabled={!canSubmit}>
            <Icon name="check" size={13} /> {isEditing ? "Save changes" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Kid-mode fields ─────────────────────────────────────────────

interface KidFieldsProps {
  icon: string;
  setIcon: (v: string) => void;
  assignmentMode: "single" | "parallel" | "rotating";
  setAssignmentMode: (v: "single" | "parallel" | "rotating") => void;
  assignee: string;
  setAssignee: (v: string) => void;
  assignees: string[];
  setAssignees: (v: string[]) => void;
  frequency: ChoreFrequency;
  setFrequency: (v: ChoreFrequency) => void;
  stars: number;
  setStars: (v: number) => void;
  requiresApproval: boolean;
  setRequiresApproval: (v: boolean) => void;
  members: HouseholdMember[];
  kids: HouseholdMember[];
}

function KidFields({
  icon, setIcon, assignmentMode, setAssignmentMode,
  assignee, setAssignee, assignees, setAssignees,
  frequency, setFrequency, stars, setStars,
  requiresApproval, setRequiresApproval, members, kids,
}: KidFieldsProps) {
  // Kids first, then adults — kid is the typical assignee for the
  // kid chart, but we don't forbid adult assignment (sometimes a
  // parent has a "remind me" item that fits the kid-chart schema).
  const ordered = useMemo(
    () => [...members.filter((m) => m.role === "kid"), ...members.filter((m) => m.role !== "kid")],
    [members],
  );
  const toggleAssignee = (userId: string) => {
    if (assignees.includes(userId)) setAssignees(assignees.filter((u) => u !== userId));
    else setAssignees([...assignees, userId]);
  };
  return (
    <>
      <div className="cr-row">
        <span>Icon</span>
        <div className="cr-icons">
          {KID_ICONS.map((e) => (
            <button
              key={e}
              type="button"
              className={`cr-icon${icon === e ? " is-selected" : ""}`}
              onClick={() => setIcon(e)}
            >{e}</button>
          ))}
        </div>
      </div>

      <div className="cr-row">
        <span>Who does it</span>
        <div className="cr-mode-toggle cr-assignment-mode">
          <button
            type="button"
            data-active={assignmentMode === "single"}
            onClick={() => setAssignmentMode("single")}
          >Just one kid</button>
          {kids.length >= 2 && (
            <button
              type="button"
              data-active={assignmentMode === "parallel"}
              onClick={() => setAssignmentMode("parallel")}
            >All</button>
          )}
          {kids.length >= 2 && (
            <button
              type="button"
              data-active={assignmentMode === "rotating"}
              onClick={() => setAssignmentMode("rotating")}
            >Rotates</button>
          )}
        </div>
      </div>

      {assignmentMode === "single" && (
        <label className="cr-row">
          <span className="cr-mini-label">Who</span>
          <select
            className="input"
            value={assignee}
            onChange={(e) => setAssignee((e.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">(unassigned — anyone)</option>
            {ordered.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.userId}{m.role === "kid" ? " (kid)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      {assignmentMode === "parallel" && (
        <div className="cr-row">
          <span className="cr-mini-label">Tap to deselect anyone who shouldn't have this</span>
          <div className="cr-assignees-multi">
            {kids.map((m) => (
              <button
                key={m.userId}
                type="button"
                className={`cr-assignee-chip${assignees.includes(m.userId) ? " is-selected" : ""}`}
                onClick={() => toggleAssignee(m.userId)}
              >{m.userId}</button>
            ))}
          </div>
        </div>
      )}

      {assignmentMode === "rotating" && (
        <div className="cr-row">
          <span className="cr-mini-label">
            Order is the rotation sequence — week 1 to the first, week 2 to the next, etc.
          </span>
          <div className="cr-assignees-multi">
            {kids.map((m) => (
              <button
                key={m.userId}
                type="button"
                className={`cr-assignee-chip${assignees.includes(m.userId) ? " is-selected" : ""}`}
                onClick={() => toggleAssignee(m.userId)}
              >
                {assignees.includes(m.userId) && (
                  <span className="cr-rotation-idx" aria-hidden>
                    {assignees.indexOf(m.userId) + 1}.
                  </span>
                )}
                {m.userId}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cr-grid">
        <label>
          <span className="cr-mini-label">How often</span>
          <select
            className="input"
            value={frequency}
            onChange={(e) => setFrequency((e.currentTarget as HTMLSelectElement).value as ChoreFrequency)}
          >
            {KID_FREQUENCIES.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="cr-mini-label">Stars per completion</span>
          <input
            className="input"
            type="number"
            min={0}
            max={20}
            value={stars}
            onInput={(e) => setStars(Math.max(0, Math.min(20, Number((e.currentTarget as HTMLInputElement).value) || 0)))}
          />
        </label>
      </div>

      <label className="cr-toggle">
        <input
          type="checkbox"
          checked={requiresApproval}
          onChange={(e) => setRequiresApproval((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>Parent approves before stars are granted</span>
      </label>
    </>
  );
}

// ── Adult-mode fields ──────────────────────────────────────────

interface AdultFieldsProps {
  category: string;
  setCategory: (v: string) => void;
  icon: string | null;
  setIcon: (v: string | null) => void;
  frequency: AdultChoreFrequency;
  setFrequency: (v: AdultChoreFrequency) => void;
  assignmentMode: "single" | "rotating" | "parallel";
  setAssignmentMode: (v: "single" | "rotating" | "parallel") => void;
  assignee: string;
  setAssignee: (v: string) => void;
  assignees: string[];
  setAssignees: (v: string[]) => void;
  hue: number;
  setHue: (v: number) => void;
  stars: number;
  setStars: (v: number) => void;
  adults: HouseholdMember[];
}

function AdultFields({
  category, setCategory, icon, setIcon, frequency, setFrequency,
  assignmentMode, setAssignmentMode,
  assignee, setAssignee, assignees, setAssignees,
  hue, setHue, stars, setStars, adults,
}: AdultFieldsProps) {
  const toggleAssignee = (userId: string) => {
    if (assignees.includes(userId)) {
      setAssignees(assignees.filter((u) => u !== userId));
    } else {
      setAssignees([...assignees, userId]);
    }
  };
  return (
    <>
      <div className="cr-row">
        <span>Icon (optional)</span>
        <div className="cr-icons">
          <button
            type="button"
            className={`cr-icon${!icon ? " is-selected" : ""}`}
            onClick={() => setIcon(null)}
            title="No icon"
            aria-label="No icon"
          >∅</button>
          {ADULT_ICONS.map((e) => (
            <button
              key={e}
              type="button"
              className={`cr-icon${icon === e ? " is-selected" : ""}`}
              onClick={() => setIcon(e)}
            >{e}</button>
          ))}
        </div>
      </div>

      <label className="cr-row">
        <span>Category</span>
        <input
          className="input"
          placeholder="e.g. Kitchen, Bathroom, Plants"
          value={category}
          onInput={(e) => setCategory((e.currentTarget as HTMLInputElement).value)}
          maxLength={40}
        />
      </label>

      <label className="cr-row">
        <span>How often</span>
        <select
          className="input"
          value={frequency}
          onChange={(e) => setFrequency((e.currentTarget as HTMLSelectElement).value as AdultChoreFrequency)}
        >
          {ADULT_FREQUENCIES.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
      </label>

      {/* Assignment mode — three-way: single, rotating, parallel.
        * "parallel" is hidden on single-adult households since it
        * requires ≥2 assignees to be useful. */}
      <div className="cr-row">
        <span>Who does it</span>
        <div className="cr-mode-toggle cr-assignment-mode">
          <button
            type="button"
            data-active={assignmentMode === "single"}
            onClick={() => setAssignmentMode("single")}
          >Just one of us</button>
          {adults.length >= 2 && (
            <button
              type="button"
              data-active={assignmentMode === "rotating"}
              onClick={() => setAssignmentMode("rotating")}
            >Rotates</button>
          )}
          {adults.length >= 2 && (
            <button
              type="button"
              data-active={assignmentMode === "parallel"}
              onClick={() => setAssignmentMode("parallel")}
            >All</button>
          )}
        </div>
      </div>

      {assignmentMode === "single" && (
        <label className="cr-row">
          <span className="cr-mini-label">Who</span>
          <select
            className="input"
            value={assignee}
            onChange={(e) => setAssignee((e.currentTarget as HTMLSelectElement).value)}
          >
            {adults.map((m) => (
              <option key={m.userId} value={m.userId}>{m.userId}</option>
            ))}
          </select>
        </label>
      )}

      {assignmentMode === "parallel" && (
        <div className="cr-row">
          <span className="cr-mini-label">Tap to deselect anyone who shouldn't have this</span>
          <div className="cr-assignees-multi">
            {adults.map((m) => (
              <button
                key={m.userId}
                type="button"
                className={`cr-assignee-chip${assignees.includes(m.userId) ? " is-selected" : ""}`}
                onClick={() => toggleAssignee(m.userId)}
              >{m.userId}</button>
            ))}
          </div>
        </div>
      )}

      <label className="cr-row">
        <span>Color</span>
        <div className="cr-hues">
          {HUE_CHOICES.map((h) => (
            <button
              key={h}
              type="button"
              data-active={hue === h}
              onClick={() => setHue(h)}
              style={{ background: `oklch(0.75 0.16 ${h})` }}
              aria-label={`hue ${h}`}
            />
          ))}
        </div>
      </label>

      <label className="cr-row">
        <span>Stars per completion (optional)</span>
        <input
          className="input"
          type="number"
          min={0}
          max={20}
          value={stars}
          onInput={(e) => setStars(Math.max(0, Math.min(20, Number((e.currentTarget as HTMLInputElement).value) || 0)))}
        />
      </label>
    </>
  );
}
